import { Injectable, inject } from "@angular/core";
import { PropertyMetadata, IGridViewMetadata } from "@tylertech/tyler-api-types";
import { MetadataPropertyService } from "../metadataPropertyService";
import { MetadataInputTypeHelperService } from "../metadataInputTypeHelperService";
import { MetadataValidationService } from "../metadataValidationService";
import { MetadataViewService } from "../metadataViewService";
import { serverVerificationKey } from "../metadataErrorService";
import { ProviderStorageService } from "./provider.storage.service";
import { CellEditService } from "./cellEdit.service";
import { Utilities } from "@tylertech/shared-module";
import { AugmentedModelObject, AugmentedPropertyMetadata } from "../../interfaces";

@Injectable()
export class GridValidationService
{
    private $metadataInputTypeHelperService = inject(MetadataInputTypeHelperService);
    private $metadataPropertyService = inject(MetadataPropertyService);
    private $metadataValidationService = inject(MetadataValidationService);
    private $metadataViewService = inject(MetadataViewService);
    private $providerStorageService = inject(ProviderStorageService);
    private $cellEditService = inject(CellEditService);

    validateCellEditGrid(providerId: string, viewMetadata: IGridViewMetadata, cellEditing: boolean): void
    {
        if (!this.$metadataViewService.IsCellEditGrid(viewMetadata) || cellEditing)
            return;
        const rows = this.$providerStorageService.GridData[viewMetadata.GridProperties["$providerId"]];
        if (Utilities.IsNullOrUndefinedOrEmpty(rows))
            return;
        const properties = viewMetadata.GridProperties.ClassMetadata.Properties;
        rows.forEach(row =>
        {
            const model = row["$$model"];
            properties.forEach((prop: AugmentedPropertyMetadata) =>
            {
                prop.$$formControlError = undefined;
                const invalid = this.isCellInvalid(prop, model) || this.hasServerError(model, prop.Name);
                this.$cellEditService.SetCellEditValidity(providerId, model, prop.Name, invalid);
            });
            this.$cellEditService.SetCellEditErrors(model, properties);
        });
    }

    hasServerError(model: AugmentedModelObject, propertyName: string): boolean
    {
        return !Utilities.IsNullOrUndefinedOrEmpty(model?.$errors?.[propertyName]?.[serverVerificationKey]);
    }

    isCellInvalid(propertyMetadata: PropertyMetadata, model: any): boolean
    {
        const viewOnly = this.$metadataPropertyService.isViewOnly(propertyMetadata, model, model["$$Parent"]);
        if (viewOnly)
            return false;
        const value = model[propertyMetadata.Name];

        const maxLength = this.$metadataInputTypeHelperService.getMaxLength(propertyMetadata, model, model["$$Parent"]);
        if (maxLength && value?.length > maxLength)
            return true;

        if (this.$metadataInputTypeHelperService.isNumeric(propertyMetadata.InputType) || propertyMetadata.InputType === "Currency" || propertyMetadata.InputType === "Percent")
        {
            if (this.$metadataPropertyService.isRequired(propertyMetadata, model, model["$$Parent"]) && Utilities.IsNullOrUndefined(value))
                return true;
            const maxValue = this.$metadataInputTypeHelperService.getMaxValue(propertyMetadata, model, model["$$Parent"]);
            if (!this.$metadataValidationService.validateMaximumAmount(value?.toString(), false, maxValue, () => this.$metadataValidationService.genericParse(value.toString())))
                return true;

            const minValue = this.$metadataInputTypeHelperService.getMinValue(propertyMetadata, model, model["$$Parent"]);
            if (!this.$metadataValidationService.validateMinimumAmount(value?.toString(), false, minValue, () => this.$metadataValidationService.genericParse(value.toString())))
                return true;

            const precision = this.$metadataInputTypeHelperService.getPrecision(propertyMetadata, model, model["$$Parent"]);
            if (!this.$metadataValidationService.validatePrecision(value?.toString(), false, precision))
                return true;

            const scale = this.$metadataInputTypeHelperService.getScale(propertyMetadata, model, model["$$Parent"]);
            if (!this.$metadataValidationService.validateScale(value?.toString(), false, scale))
                return true;
        }
        else
        {
            if (this.$metadataPropertyService.isRequired(propertyMetadata, model, model["$$Parent"]) && !value)
                return true;
        }

        return false;
    }
}
